import {
  SegmentedGroup,
  SegmentedGroupItem,
} from '@repo/ui/components/segmented-group'
import { useFeatureFlag, useTranslate } from '@repo/shared/hooks'
import { cn } from '@repo/ui/lib/utils'
import type { OrderBoxTabsProps, OrderBoxTab } from './order-box.types'

export const OrderBoxTabs = ({ tab, onTabChange }: OrderBoxTabsProps) => {
  const { t } = useTranslate()

  const { isFlagEnabled: isBuyFlagEnabled } = useFeatureFlag(
    ['quick-trade', 'value', 'buy'],
    { returnBoolean: true },
  )
  const { isFlagEnabled: isSellFlagEnabled } = useFeatureFlag(
    ['quick-trade', 'value', 'sell'],
    { returnBoolean: true },
  )
  const { isFlagEnabled: isConvertFlagEnabled } = useFeatureFlag(
    ['quick-trade', 'value', 'convert'],
    { returnBoolean: true },
  )

  // Determine which tabs are enabled
  const enabledTabs = [
    isBuyFlagEnabled && 'buy',
    isSellFlagEnabled && 'sell',
    isConvertFlagEnabled && 'convert',
  ].filter(Boolean) as OrderBoxTab[]

  const enabledCount = enabledTabs.length

  // Calculate basis percentage based on enabled tabs count
  const getBasisClass = (isActive: boolean) => {
    if (enabledCount === 1) return 'basis-full'
    if (enabledCount === 2)
      return isActive ? 'basis-[calc(50%_+_8px)]' : 'basis-1/2'
    if (enabledCount === 3)
      return isActive
        ? 'basis-[calc(33.333%_+_5.33px)] !w-[33.33%]'
        : 'basis-[33.333%] !w-[33.33%]'
    return 'basis-[33.333%]'
  }

  // Get styling classes based on tab position and enabled tabs
  const getTabClasses = (tabValue: OrderBoxTab, isActive: boolean) => {
    const baseClasses = 'text-center text-sm p-sm'
    if (!isActive) {
      return cn(
        baseClasses,
        getBasisClass(false),
        'text-neutral-surface-100 bg-neutral-surface-dark rounded-md',
      )
    }

    // Single tab scenario
    if (enabledCount === 1) {
      return cn(
        baseClasses,
        getBasisClass(true),
        '!filter-none pl-[16px] !bg-positive-main !text-positive-main-100 !font-normal rtl:!rounded-bl-none ltr:!rounded-br-none rtl:!rounded-tl-none ltr:!rounded-tr-none',
      )
    }

    // Two tabs scenario (buy + sell)
    if (
      enabledCount === 2 &&
      enabledTabs.includes('buy') &&
      enabledTabs.includes('sell')
    ) {
      const isLastTab = enabledTabs.indexOf(tabValue) === 1
      if (isLastTab) {
        return cn(
          baseClasses,
          getBasisClass(true),
          '!filter-none pr-[16px] !bg-negative-main !text-negative-main-100 !font-normal rtl:!rounded-br-none ltr:!rounded-bl-none rtl:!rounded-tr-none ltr:!rounded-tl-none rtl:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)] ltr:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)]',
        )
      }
      // First tab (buy)
      return cn(
        baseClasses,
        getBasisClass(true),
        '!filter-none pl-[16px] !bg-positive-main !text-positive-main-100 !font-normal rtl:!rounded-bl-none ltr:!rounded-br-none rtl:!rounded-tl-none ltr:!rounded-tr-none rtl:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)] ltr:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)]',
      )
    }

    // Two tabs scenario (buy + convert)
    if (
      enabledCount === 2 &&
      enabledTabs.includes('buy') &&
      enabledTabs.includes('convert')
    ) {
      const isLastTab = enabledTabs.indexOf(tabValue) === 1
      if (isLastTab) {
        // Convert tab (last)
        return cn(
          baseClasses,
          getBasisClass(true),
          '!filter-none pr-[16px] !bg-base-main !text-base-main-100 !font-normal rtl:!rounded-br-none ltr:!rounded-bl-none rtl:!rounded-tr-none ltr:!rounded-tl-none rtl:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)] ltr:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)]',
        )
      }
      // First tab (buy)
      return cn(
        baseClasses,
        getBasisClass(true),
        '!filter-none pl-[16px] !bg-positive-main !text-positive-main-100 !font-normal rtl:!rounded-bl-none ltr:!rounded-br-none rtl:!rounded-tl-none ltr:!rounded-tr-none rtl:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)] ltr:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)]',
      )
    }

    // Two tabs scenario (sell + convert)
    if (
      enabledCount === 2 &&
      enabledTabs.includes('sell') &&
      enabledTabs.includes('convert')
    ) {
      const isLastTab = enabledTabs.indexOf(tabValue) === 1
      if (isLastTab) {
        // Convert tab (last)
        return cn(
          baseClasses,
          getBasisClass(true),
          '!filter-none pr-[16px] !bg-base-main !text-base-main-100 !font-normal rtl:!rounded-br-none ltr:!rounded-bl-none rtl:!rounded-tr-none ltr:!rounded-tl-none rtl:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)] ltr:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)]',
        )
      }
      // First tab (sell)
      return cn(
        baseClasses,
        getBasisClass(true),
        '!filter-none pl-[16px] !bg-negative-main !text-negative-main-100 !font-normal rtl:!rounded-bl-none ltr:!rounded-br-none rtl:!rounded-tl-none ltr:!rounded-tr-none rtl:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)] ltr:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)]',
      )
    }

    // Three tabs scenario
    if (enabledCount === 3) {
      const tabIndex = enabledTabs.indexOf(tabValue)
      const isFirstTab = tabIndex === 0
      const isMiddleTab = tabIndex === 1
      const isLastTab = tabIndex === 2

      if (isFirstTab) {
        // Buy tab (first)
        return cn(
          baseClasses,
          getBasisClass(true),
          '!filter-none pl-[16px] !bg-positive-main !text-positive-main-100 !font-normal rtl:!rounded-bl-none ltr:!rounded-br-none rtl:!rounded-tl-none ltr:!rounded-tr-none rtl:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)] ltr:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)]',
        )
      }

      if (isMiddleTab) {
        // Sell tab (middle)
        return cn(
          baseClasses,
          getBasisClass(false),
          '!rounded-none !filter-none pr-[16px] !bg-negative-main !text-negative-main-100 !font-normal rtl:[clip-path:polygon(8px_0,100%_0,calc(100%_-_8px)_100%,0_100%,0_100%)] ltr:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)]',
        )
      }

      if (isLastTab) {
        // Convert tab (last)
        return cn(
          baseClasses,
          getBasisClass(false),
          '!filter-none pr-[16px] !bg-base-main !text-base-main-100 !font-normal rtl:!rounded-br-none ltr:!rounded-bl-none rtl:!rounded-tr-none ltr:!rounded-tl-none rtl:[clip-path:polygon(0_0,_100%_0,_calc(100%_-_8px)_100%,_0_100%)] ltr:[clip-path:polygon(8px_0,_100%_0,_100%_100%,_0_100%)]',
        )
      }
    }

    return cn(baseClasses, getBasisClass(true))
  }

  return (
    <SegmentedGroup
      value={tab}
      onValueChange={onTabChange}
      size='lg'
      className='w-full min-h-[30px]'
      noIndicatorBg={true}
    >
      {isBuyFlagEnabled && (
        <SegmentedGroupItem
          value='buy'
          className={getTabClasses('buy', tab === 'buy')}
        >
          {t('order_buy')}
        </SegmentedGroupItem>
      )}
      {isSellFlagEnabled && (
        <SegmentedGroupItem
          value='sell'
          className={getTabClasses('sell', tab === 'sell')}
        >
          {t('order_sell')}
        </SegmentedGroupItem>
      )}
      {isConvertFlagEnabled && (
        <SegmentedGroupItem
          value='convert'
          className={getTabClasses('convert', tab === 'convert')}
        >
          {t('order_convert')}
        </SegmentedGroupItem>
      )}
    </SegmentedGroup>
  )
}
